import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Table, Badge, Alert, Spinner, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import InviteModal from './InviteModal';
import {
  useFetchLeagueInvitesQuery,
  useDeleteInvitationMutation,
  useAssignUserToLeagueMutation,
} from '../slices/invitationApiSlice';
import { useRemoveLeagueMemberMutation } from '../slices/leaguesApiSlice';

const getIdString = (value) => {
  const candidate = value?._id || value;
  return candidate ? String(candidate) : null;
};

const isPersonalTeamForAthlete = (team, athlete) => {
  const teamId = getIdString(team);
  const athleteTeamId = getIdString(athlete?.team);
  const teamAdminId = getIdString(team?.admin);
  const athleteUserId = getIdString(athlete?.user);

  return (teamId && athleteTeamId && teamId === athleteTeamId)
    || (teamAdminId && athleteUserId && teamAdminId === athleteUserId);
};

const isRosteredByAnotherLeagueTeam = (athlete, leagueTeams = []) => {
  const athleteId = getIdString(athlete);
  if (!athleteId) {
    return false;
  }

  return leagueTeams.some((team) => {
    const rosterAthleteIds = [
      ...(team?.athlete || []).map(getIdString),
      getIdString(team?.benchAthlete),
    ].filter(Boolean);

    return rosterAthleteIds.includes(athleteId) && !isPersonalTeamForAthlete(team, athlete);
  });
};

const getAthleteDisplayName = (athlete) => [athlete?.firstName, athlete?.lastName].filter(Boolean).join(' ')
  || athlete?.user?.username
  || 'Unnamed athlete';

const LeagueMembershipAdminTools = ({
  manualEmail,
  onManualEmailChange,
  onAssignUser,
  isAssigning,
}) => (
  <>
    <Alert variant="secondary" className="mb-3">
      <div className="fw-semibold mb-2">Need to repair an invite?</div>
      <div className="small text-muted mb-3">
        If someone accepted an invitation but still cannot see the league, assign them here by the email address on their account.
      </div>
      <Form
        className="d-flex gap-2 flex-wrap"
        onSubmit={(e) => {
          e.preventDefault();
          onAssignUser();
        }}
      >
        <Form.Control
          type="email"
          placeholder="member@example.com"
          value={manualEmail}
          onChange={(e) => onManualEmailChange(e.target.value)}
          style={{ maxWidth: '320px' }}
        />
        <Button type="submit" variant="outline-primary" disabled={isAssigning}>
          {isAssigning ? 'Assigning...' : 'Assign Existing User'}
        </Button>
      </Form>
    </Alert>
  </>
);

LeagueMembershipAdminTools.propTypes = {
  manualEmail: PropTypes.string.isRequired,
  onManualEmailChange: PropTypes.func.isRequired,
  onAssignUser: PropTypes.func.isRequired,
  isAssigning: PropTypes.bool.isRequired,
};

const InvitationManager = ({
  leagueId,
  leagueName,
  isOwner = false,
  memberCount,
  athletes = [],
  leagueTeams = [],
  hasActiveSeason = false,
  showAdminTools = true,
  shouldOpenInviteModal = false,
  onInviteModalOpened,
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [manualEmail, setManualEmail] = useState('');

  // Use Redux API slice for data fetching
  const { data: invitationsData, error, isLoading, refetch } = useFetchLeagueInvitesQuery(leagueId, {
    skip: !leagueId || !isOwner,
  });

  const [deleteInvitation, { isLoading: isDeleting }] = useDeleteInvitationMutation();
  const [assignUserToLeague, { isLoading: isAssigning }] = useAssignUserToLeagueMutation();
  const [removeLeagueMember, { isLoading: isRemovingMember }] = useRemoveLeagueMemberMutation();

  const invitations = invitationsData?.invitations || [];
  const visibleMembers = athletes.filter((athlete) => {
    if (!athlete?._id) {
      return false;
    }

    if (!hasActiveSeason) {
      return true;
    }

    return !isRosteredByAnotherLeagueTeam(athlete, leagueTeams);
  });

  const combinedRowsByKey = new Map();

  invitations.forEach((invitation) => {
    const normalizedEmail = invitation?.invitedEmail?.trim().toLowerCase();
    const rowKey = normalizedEmail || `invitation-${invitation._id}`;

    combinedRowsByKey.set(rowKey, {
      key: rowKey,
      athlete: null,
      invitation,
      name: 'Invitation only',
      email: invitation?.invitedEmail || 'No email on file',
    });
  });

  visibleMembers.forEach((athlete) => {
    const athleteEmail = athlete?.user?.email || '';
    const normalizedEmail = athleteEmail.trim().toLowerCase();
    const rowKey = normalizedEmail || `member-${athlete._id}`;
    const existingRow = combinedRowsByKey.get(rowKey);

    combinedRowsByKey.set(rowKey, {
      key: rowKey,
      athlete,
      invitation: existingRow?.invitation || null,
      name: getAthleteDisplayName(athlete),
      email: athleteEmail || existingRow?.email || 'No email on file',
    });
  });

  const combinedRows = Array.from(combinedRowsByKey.values()).sort((left, right) => {
    const membershipPriority = Number(Boolean(right.athlete)) - Number(Boolean(left.athlete));
    if (membershipPriority !== 0) {
      return membershipPriority;
    }

    return left.email.localeCompare(right.email);
  });

  useEffect(() => {
    if (!shouldOpenInviteModal) {
      return;
    }

    setShowInviteModal(true);
    onInviteModalOpened?.();
  }, [onInviteModalOpened, shouldOpenInviteModal]);

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    try {
      await deleteInvitation(invitationId).unwrap();
      toast.success('Invitation deleted successfully');
      refetch(); // Refetch the invitations
    } catch (err) {
      toast.error('Failed to delete invitation');
    }
  };

  const handleInviteSent = () => {
    refetch(); // Refetch invitations when a new one is sent
  };

  const handleCopyInvite = async (invitation) => {
    const inviteUrl = `${window.location.origin}/invite/${invitation._id}`;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
      } else {
        throw new Error('Clipboard not supported');
      }

      toast.success('Invitation link copied to your clipboard. You can now paste and send it to the athlete.');
    } catch (err) {
      toast.error('Failed to copy the invitation link to your clipboard');
    }
  };

  const handleAssignUser = async (emailOverride) => {
    const email = (emailOverride || manualEmail).trim();
    if (!email) {
      toast.error('Enter an email address first.');
      return;
    }

    try {
      await assignUserToLeague({ leagueId, email }).unwrap();
      toast.success(`Assigned ${email} to ${leagueName}.`);
      setManualEmail('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to assign user to league');
    }
  };

  const handleRemoveMember = async (athlete) => {
    const athleteName = getAthleteDisplayName(athlete)
      || athlete?.user?.email
      || 'this user';

    if (!window.confirm(`Remove ${athleteName} from ${leagueName}? This removes them from the next draft and season setup while keeping past completed seasons intact.`)) {
      return;
    }

    try {
      await removeLeagueMember({ leagueId, athleteId: athlete._id }).unwrap();
      toast.success(`${athleteName} was removed from ${leagueName}.`);
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to remove user from league');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'accepted':
        return <Badge bg="success">Accepted</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  if (!isOwner) {
    return null; // Don't show invitation manager to non-owners
  }

  if (isLoading) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-center">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading invitations...</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Membership and Invitations</h5>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowInviteModal(true)}
        >
          Send Invitation
        </Button>
      </Card.Header>
      
      <Card.Body>
        {typeof memberCount === 'number' && (
          <Alert variant="info" className="mb-3">
            {memberCount <= 1
              ? 'Your league is ready to grow! Invite others to get started.'
              : memberCount < 8
                ? `Your league is growing! There are ${memberCount} out of 8 minimum members so far.`
                : memberCount < 24
                  ? `Your league has grown! You have ${memberCount} out of a maximum of 24 members!`
                  : 'Your league is full! Great job. Time to get some miles in!'}
          </Alert>
        )}

        {hasActiveSeason && (
          <Alert variant="warning" className="mb-3">
            Member removal is disabled while a season is active. Once the current season ends, you can remove athletes from the next draft and season setup without changing past season history.
          </Alert>
        )}

        {showAdminTools && (
          <LeagueMembershipAdminTools
            manualEmail={manualEmail}
            onManualEmailChange={setManualEmail}
            onAssignUser={handleAssignUser}
            isAssigning={isAssigning}
          />
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error.data?.message || error.message || 'Failed to load invitations'}
          </Alert>
        )}

        {combinedRows.length === 0 ? (
          <p className="text-muted text-center">
            No members or invitations are listed yet. Click Send Invitation to invite members to your league.
          </p>
        ) : (
          <Table responsive size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Member Status</th>
                <th>Invite Status</th>
                <th>Date Sent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {combinedRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.name}</td>
                  <td>{row.email}</td>
                  <td>
                    {row.athlete
                      ? <Badge bg="secondary">League member</Badge>
                      : <span className="text-muted">Invitation only</span>}
                  </td>
                  <td>
                    {row.invitation ? getStatusBadge(row.invitation.status) : <span className="text-muted">No invite</span>}
                  </td>
                  <td>{row.invitation ? new Date(row.invitation.createdAt).toLocaleDateString() : <span className="text-muted">-</span>}</td>
                  <td>
                    {row.athlete && !hasActiveSeason && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="me-2"
                        onClick={() => handleRemoveMember(row.athlete)}
                        disabled={isRemovingMember}
                      >
                        {isRemovingMember ? 'Removing...' : 'Remove'}
                      </Button>
                    )}
                    {row.invitation && (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleAssignUser(row.invitation.invitedEmail)}
                          disabled={isAssigning}
                        >
                          {isAssigning ? 'Assigning...' : 'Assign User'}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleCopyInvite(row.invitation)}
                        >
                          Copy Invite
                        </Button>
                        {row.invitation.status === 'pending' && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteInvitation(row.invitation._id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <InviteModal
        show={showInviteModal}
        onHide={() => setShowInviteModal(false)}
        leagueId={leagueId}
        leagueName={leagueName}
        onInviteSent={handleInviteSent}
      />
    </Card>
  );
};

InvitationManager.propTypes = {
  leagueId: PropTypes.string,
  leagueName: PropTypes.string,
  isOwner: PropTypes.bool,
  memberCount: PropTypes.number,
  athletes: PropTypes.array,
  leagueTeams: PropTypes.array,
  hasActiveSeason: PropTypes.bool,
  showAdminTools: PropTypes.bool,
  shouldOpenInviteModal: PropTypes.bool,
  onInviteModalOpened: PropTypes.func,
};

export default InvitationManager;
